import React, {lazy, Suspense, useCallback, useState} from "react";
import {useSearchResultDetailsState} from "../search-result-details/SearchResultDetailsContext";
import {CONVERSION_DONE, useObjectConversionDispatch,} from "./ObjectConversionContext";
import Stack from "@mui/material/Stack";
import {getMimeTypeFromModel} from "../utility";
import {CopyrightClauseWarning} from "./CopyrightClauseWarning";
import Box from "@mui/material/Box";

const ObjectMediaImage = lazy(() => import('./ObjectMediaImage'));
const ObjectMediaPdf = lazy(() => import('./ObjectMediaPdf'));
const ObjectMediaDocument = lazy(() => import('./ObjectMediaDocument'));
const ObjectMediaVideo = lazy(() => import('./ObjectMediaVideo'));
const ObjectMediaAudio = lazy(() => import('./ObjectMediaAudio'));
const ObjectMediaPlayIcon = lazy(() => import('./ObjectMediaPlayIcon'));
const ObjectViewCopyrightIconsOverlay = lazy(() => import('./ObjectViewCopyrightIconsOverlay'));
const ObjectConversionStatus = lazy(() => import('./ObjectConversionStatus'));
const OpenIn3DButton = lazy(() => import('./OpenIn3DButton'));
const ThreeDViewerDialog = lazy(() => import('./ThreeDViewerDialog'));

// Defined outside the component to avoid re-creating the array on every render.
const DOCUMENT_ICON_MODEL_TYPES = ["Geodata", "Modell", "Tabell", "Misc", "Dokument"];

export const ObjectViewMediaSection = ({
                                           t,
                                           gridRef,
                                           mediaPlayCallback,
                                           smallScreen,
                                           pdfOpenedCallback
                                       }) => {
    const {model, fullscreen, mediaPlaying} = useSearchResultDetailsState();
    const dispatch = useObjectConversionDispatch();

    const {collectionId, documentType} = model;
    const mimeType = getMimeTypeFromModel(model);

    const [showViewer, setShowViewer] = useState(false);

    const showViewerCallback = useCallback(show => setShowViewer(show), []);
    const closeViewerCallback = useCallback(() => setShowViewer(false), []);

    return (mimeType && <>
            <Stack
                sx={{
                    display: "flex",
                    flexFlow: 'row nowrap',
                    justifyContent: "center",
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    minWidth: '100%',
                    minHeight: '100%',
                }}
            >
                {/* Show copyright icons - if any... */}
                <Suspense fallback={<></>}>
                    <ObjectViewCopyrightIconsOverlay fullscreen={fullscreen} model={model}/>
                </Suspense>

                {!mediaPlaying &&
                    !DOCUMENT_ICON_MODEL_TYPES.includes(documentType) && documentType !== "Audio" && (
                        <Suspense fallback={<></>}>
                            <ObjectMediaImage parentRef={gridRef} smallScreen={smallScreen}/>
                        </Suspense>
                    )}

                {!mediaPlaying && mimeType.indexOf("pdf") !== -1 && (
                    <Suspense fallback={<></>}>
                        <ObjectMediaPdf parentRef={gridRef} smallScreen={smallScreen}
                                        pdfOpenedCallback={pdfOpenedCallback}/>
                    </Suspense>
                )}

                {/* Document icon */}
                {!mediaPlaying &&
                    mimeType.indexOf("pdf") === -1 &&
                    DOCUMENT_ICON_MODEL_TYPES.includes(documentType) && (
                        <Suspense fallback={<></>}>
                            <ObjectMediaDocument
                                smallScreen={smallScreen}
                                documentType={documentType}
                            />
                        </Suspense>
                    )}

                {/* Videoplayer */}
                {mediaPlaying && documentType === "Video" && (
                    <Suspense fallback={<></>}>
                        <ObjectMediaVideo smallScreen={smallScreen} parentRef={gridRef}/>
                    </Suspense>
                )}

                {/* Audioplayer */}
                {documentType === "Audio" && (
                    <Suspense fallback={<></>}>
                        <ObjectMediaAudio smallScreen={smallScreen}/>
                    </Suspense>
                )}

                {/* Play overlay icon for audio/video */}
                <Suspense fallback={<></>}>
                    <ObjectMediaPlayIcon onClick={mediaPlayCallback} smallScreen={smallScreen}/>
                </Suspense>

                {/* 3D "open in viewer" button and 3D viewer */}
                <Suspense fallback={<></>}>
                    <OpenIn3DButton model={model} showViewerCallback={showViewerCallback}/>
                    <ThreeDViewerDialog model={model} closeViewerCallback={closeViewerCallback}
                                        showDialog={showViewer}/>
                </Suspense>

                {/* Media object conversion status */}
                {["Video", "Audio"].includes(documentType) && (
                    <ObjectConversionStatus
                        t={t}
                        collectionId={collectionId}
                        smallScreen={smallScreen}
                        callback={() => dispatch({type: CONVERSION_DONE})}
                    />
                )}

                {!smallScreen && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: '25%',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '16px',
                    }}>
                        <CopyrightClauseWarning t={t}/>
                    </Box>
                )}
            </Stack>
        </>
    );
};
